// Central launch-window clock. Every opening-day surface (pre-launch splash,
// celebratory popup, OPEN-XXXX promo card, waitlist raffle) reads its phase
// from here so we can flip the whole site's tone with a single hook.
//
// Timeline (all times America/New_York):
//   PRE           — everything before Aug 29 2026 00:00 ET
//                   → splash + raffle + waitlist + OPEN-XXXX + countdown to open
//   OPENING_DAY   — Aug 29 2026 all day (00:00 → Aug 30 00:00 ET)
//                   → celebratory popup fires once, splash gone, raffle gone
//                     (winner announced), OPEN-XXXX still visible w/ countdown
//   CODE_ACTIVE_TAIL — Aug 30 2026 00:00 → 00:01 ET (rare 60-second sliver)
//                   → OPEN-XXXX still visible but popup no longer fires
//   CLOSED        — Aug 30 2026 00:01 ET onward
//                   → All opening banners hidden. Site behaves like a
//                     normal established business.
//
// August 2026 is Eastern Daylight Time (UTC-4). DST doesn't roll back until
// Nov 2 2026, so we bake -04:00 into the ISO strings directly. Using explicit
// offsets keeps timing correct regardless of the visitor's local timezone.
import { useEffect, useState } from "react";

export const OPEN_ISO = "2026-08-29T00:00:00-04:00";       // opening midnight ET
export const OPEN_END_ISO = "2026-08-30T00:00:00-04:00";   // 24hr later
export const CODE_EXPIRES_ISO = "2026-08-30T00:01:00-04:00"; // sliver past
// 1-year anniversary window — DST is still on Aug 29 2027 (rolls back Nov 7),
// so EDT (-04:00) is still the right offset.
export const ANNIVERSARY_ISO = "2027-08-29T00:00:00-04:00";
export const ANNIVERSARY_END_ISO = "2027-08-30T00:00:00-04:00";
// Anniversary discount code — seeded on the backend at startup so the
// booking flow validates it live. Kept in this shared clock file so the
// popup + validator stay lock-stepped.
export const ANNIVERSARY_CODE = "BBB1YEAR";

export const OPEN_TS = Date.parse(OPEN_ISO);
export const OPEN_END_TS = Date.parse(OPEN_END_ISO);
export const CODE_EXPIRES_TS = Date.parse(CODE_EXPIRES_ISO);
export const ANNIVERSARY_TS = Date.parse(ANNIVERSARY_ISO);
export const ANNIVERSARY_END_TS = Date.parse(ANNIVERSARY_END_ISO);

// Snapshot the phase for `now`. Extracted so both the pre-mount check and
// the interval tick share the same logic.
//
// Sprint 13.75 (post-opening cleanup): once the grand-opening day passed,
// the owner asked to strip every countdown, splash, and raffle strip from
// the site so the app behaves as an established business. We short-circuit
// the phase to "CLOSED" and treat the anniversary window as inert too —
// nothing on the site is calendar-gated anymore. Keeping the ISO
// constants + splitDuration helper around because a handful of legacy
// backend fields (booking `off_day_service`, promo cards) still reference
// them, and re-introducing seasonal banners next year should be a
// one-line revert here.
export function computePhase(_now = Date.now()) {
  return "CLOSED";
}

// React hook — returns { phase, msToOpen, msToCodeExpiry } and re-renders
// every second so countdowns tick live. Cheap: interval is torn down when
// the last consumer unmounts. Never leaves an interval running after the
// launch window has fully closed (returns without scheduling one).
export function useOpeningPhase() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // Stop ticking after the anniversary window closes — nothing left to
    // count down until the following year's window arrives.
    if (Date.now() >= ANNIVERSARY_END_TS) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const phase = computePhase(now);
  return {
    phase,
    now,
    msToOpen: Math.max(0, OPEN_TS - now),
    msToCodeExpiry: Math.max(0, CODE_EXPIRES_TS - now),
    msToAnniversaryEnd: Math.max(0, ANNIVERSARY_END_TS - now),
    codeStillLive: now < CODE_EXPIRES_TS,
    isOpeningDay: phase === "OPENING_DAY",
    isAnniversary: phase === "ANNIVERSARY",
    preLaunch: phase === "PRE",
    postLaunch: phase === "CLOSED" || phase === "ANNIVERSARY",
  };
}

// Split `ms` into days/hours/minutes/seconds tuple for countdown UIs.
export function splitDuration(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  };
}
